import { useEffect, useState } from 'react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { GatedButton } from '../ui/GatedButton'
import { TextArea } from '../ui/Input'
import { SectionLabel } from '../ui/SectionLabel'
import { Pill } from '../ui/Pill'
import { useReportSubmission } from '../../hooks/useReportSubmission'
import { ReportAttachments, ReportEvidenceStatus } from './ReportAttachments'
import type { CotReportReason } from '../../types/community'

const KID_REASONS: Array<{ value: CotReportReason; label: string }> = [
  { value: 'Unkind', label: 'They were mean or left me out' },
  { value: 'PrivateInfo', label: 'They asked me something private' },
  { value: 'ScaryAdult', label: 'They said something scary or rude' },
  { value: 'Griefing', label: 'They wrecked our build on purpose' },
]

interface KidReportModalProps {
  open: boolean
  onClose: () => void
  targetName: string
  guardianFirst: string
  actingPersonId?: string
  circleId?: string
  targetPersonId?: string
  targetHandle?: string
}

/** Kid report modal (COTC_SafetyKudos R4 / DESIGN §3.12). Choose → detail → sent. */
export function KidReportModal({
  open, onClose, targetName, guardianFirst, circleId, targetPersonId, targetHandle,
}: KidReportModalProps) {
  const submission = useReportSubmission()
  const [fileError, setFileError] = useState<string | null>(null)
  const [step, setStep] = useState<'choose' | 'detail' | 'sent'>('choose')
  const [reason, setReason] = useState<CotReportReason | null>(null)
  const [detail, setDetail] = useState('')
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [files, setFiles] = useState<File[]>([])

  useEffect(() => {
    if (!open) return
    submission.reset(); setFileError(null); setStep('choose'); setReason(null); setDetail(''); setDate(''); setTime(''); setFiles([])
    // Reset only when reopening, never during an in-flight upload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  const reasonLabel = KID_REASONS.find((r) => r.value === reason)?.label ?? ''

  async function send() {
    if (!reason) return
    await submission.send({ circleId, targetPersonId, targetHandle, reason, detail: detail.trim() || undefined, incidentDate: date || undefined, incidentTime: time || undefined, blockRequested: true, notifyCircle: false }, files)
  }
  const currentStep = submission.receipt ? 'sent' : step

  return (
    <Modal open={open} onClose={() => { if (!submission.busy) onClose() }} title={`Report ${targetName}`}>
      <div className="animate-rise">
        <SectionLabel>Reporting {targetName}</SectionLabel>

        {currentStep === 'choose' && (
          <div className="mt-3">
            <h2 className="font-serif text-[26px] font-medium text-ink">What happened?</h2>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">
              You will not get in trouble. Pick whatever fits best — you can add more later.
            </p>
            <div className="mt-4 grid gap-2">
              {KID_REASONS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => { setReason(r.value); setStep('detail') }}
                  className="rounded-btn border border-line px-4 py-3 text-left text-[13.5px] font-medium text-ink transition-colors hover:border-terra"
                >
                  {r.label}
                </button>
              ))}
            </div>
            <div className="mt-5 flex justify-start">
              <Button variant="outline" onClick={onClose}>Never mind</Button>
            </div>
          </div>
        )}

        {currentStep === 'detail' && (
          <div className="mt-3">
            <div className="mb-3"><Pill tone="honey">{reasonLabel}</Pill></div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="text-[12px] text-ink-muted">When (date)
                <input disabled={submission.busy} type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full rounded-input border border-line bg-paper-surface px-3 py-2 text-[13px]" />
              </label>
              <label className="text-[12px] text-ink-muted">Time
                <input disabled={submission.busy} type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1 w-full rounded-input border border-line bg-paper-surface px-3 py-2 text-[13px]" />
              </label>
            </div>
            <TextArea disabled={submission.busy} label="Report details" className="mt-3" rows={3} placeholder="Tell us what happened." value={detail} onChange={(e) => setDetail(e.target.value)} />
            <ReportAttachments child files={files} onChange={setFiles} onError={setFileError} disabled={submission.busy} />
            {fileError && <p role="alert" className="mt-2 text-[13px] text-honey-fg">{fileError}</p>}
            {submission.error && <p role="alert" className="mt-2 text-[13px] text-honey-fg">{submission.error} Delivery could not be confirmed. Please check your reports before retrying.</p>}
            <div className="mt-5 flex justify-between">
              <Button variant="outline" disabled={submission.busy} onClick={() => setStep('choose')}>Back</Button>
              <GatedButton gated={false} disabled={submission.busy} hint="Sending…" variant="accent" onClick={send}>{submission.busy ? 'Sending report and attachments…' : 'Send to a moderator'}</GatedButton>
            </div>
          </div>
        )}

        {currentStep === 'sent' && (
          <div className="mt-3 rounded-card bg-moss-bg p-5">
            <h2 className="font-serif text-[26px] font-medium text-moss">Thank you for telling us.</h2>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">
              Your report was received and queued for review, and {guardianFirst || 'your grown-up'} can see it in the safety centre. {submission.receipt?.blockApplied === true ? `${targetName} is hidden from your family’s supported community views.` : ''} They are not told who reported them.
            </p>
            <ReportEvidenceStatus failedFiles={submission.failedFiles} uploadedCount={submission.uploadedCount} busy={submission.busy} onRemove={submission.removeFailedFile} onRetry={() => void submission.retryAttachments()} />
            <div className="mt-5 flex justify-end">
              <Button variant="ink" disabled={submission.busy} onClick={onClose}>Done</Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
